import React from 'react';
import { BuildingOfficeIcon, GlobeAltIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { useLearnerProfile } from '../../hooks/learner/useLearnerProfile';

const PLAN_LABEL: Record<string, string> = {
  starter: 'Starter',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

export default function LearnerOrganizationCard() {
  const { activeMembership } = useAuth();
  const tenant = activeMembership?.tenant ?? null;
  const { data: profile } = useLearnerProfile();

  if (!tenant) {
    return (
      <div className="card p-6">
        <p className="text-sm text-text-secondary italic">Organization details unavailable.</p>
      </div>
    );
  }

  // Org-assignment fields live on the learner profile (GET /me/profile); the
  // unified auth session only carries the membership role.
  const roleLabel = profile?.role_name ?? activeMembership?.role ?? null;

  return (
    <div className="space-y-4">
      <div className="card p-6">
        <h2 className="text-base font-semibold text-text-primary mb-4">Organization</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <Field icon={BuildingOfficeIcon} label="Name" value={tenant.name ?? '—'} />
          <Field icon={GlobeAltIcon} label="Domain" value={tenant.domain ?? '—'} />
          <Field
            icon={SparklesIcon}
            label="Plan"
            value={tenant.subscription_plan ? PLAN_LABEL[tenant.subscription_plan] ?? tenant.subscription_plan : '—'}
          />
        </dl>
      </div>

      {(roleLabel || profile?.department_name || profile?.location_name) && (
        <div className="card p-6">
          <h2 className="text-base font-semibold text-text-primary mb-4">Your Assignment</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            {roleLabel && <Field label="Role" value={roleLabel} />}
            {profile?.department_name && <Field label="Department" value={profile.department_name} />}
            {profile?.location_name && <Field label="Location" value={profile.location_name} />}
          </dl>
        </div>
      )}

      <p className="text-xs text-text-tertiary px-1">
        Need a change to your organization? Reach out to your team admin — these details are managed
        by your trainers.
      </p>
    </div>
  );
}

interface FieldProps {
  icon?: React.ElementType;
  label: string;
  value: string;
}

function Field({ icon: Icon, label, value }: FieldProps) {
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="w-4 h-4 text-text-tertiary mt-0.5 flex-shrink-0" />}
      <div>
        <dt className="text-text-tertiary text-xs uppercase tracking-wide">{label}</dt>
        <dd className="font-medium text-text-primary">{value}</dd>
      </div>
    </div>
  );
}
